<template>
  <div class="flex-auto flex flex-column scheduling-body upgrade-page">
    <span class="close-upgrade-button" style="display: none" @click="$root.$emit('toggle-upgrade', false)">
      <i class="la la-times big-text"></i>
    </span>
    <perfect-scrollbar class="flex-auto p-20 px-10-xs" ref="wrapper">
      <h5 class="strong color-white color-white-darkmode align-center mb-20">{{ me.userName }}, let's upgrade your Weezly account</h5>

      <div class="scheduling-content expand relative">
        <Billing :class="{ 'animated faster fadeInUp': animated }" v-if="open_billing" :billing="billing" @back="billingBack" @updated="loadData" />
        <SwitchPlan
          :class="{ 'animated faster fadeInUp': animated }"
          v-else-if="open_switch"
          :billing="billing"
          :plan_type="plan_name + '_' + billing_cycle"
          @back="open_switch = false"
          @updated="loadData"
          ref="switchPlan" />
        <div v-else class="bg-white border-radius shadow p-20 px-10-xs relative" :class="{ 'animated faster fadeInUp': animated }">
          <div class="mb-20 flex items-center relative" style="z-index: 4">
            <!-- Back button -->
            <button
              class="btn btn-outline free-width px-15"
              v-ripple
              :class="{ invisible: status != 'entry-active' && status != 'growth-active' && status != 'agency-active' }"
              @click="
                status = '';
                show_upgrade_form = false;
              ">
              <i class="la la-arrow-left big-text"></i>
              <span class="hide-xs ml-5">Back</span>
            </button>

            <div class="flex-1"></div>

            <!-- Open billing button -->
            <button class="btn btn-outline free-width px-15 px-5-xs" v-ripple @click="open_billing = true">
              <i class="la la-credit-card font-h4"></i>
              <span class="hide-xs ml-5">Billing</span>
            </button>
          </div>

          <div class="upgrade-wrapper" :class="[{ 'is-blackfriday': billing.blackfriday }, status]">
            <div class="left-element">
              <EntryPlan @choose="choosePlan" @switch="switchPlan" :show_upgrade_form="show_upgrade_form" :billing_cycle="billing_cycle" :billing="billing" />
            </div>
            <div class="middle-element relative">
              <GrowthBlackfriday
                v-if="billing.blackfriday"
                @choose="choosePlan"
                @switch="switchPlan"
                :show_upgrade_form="show_upgrade_form"
                :billing_cycle="billing_cycle"
                :billing="billing" />
              <GrowthPlan
                v-else
                @choose="choosePlan"
                @switch="switchPlan"
                :show_upgrade_form="show_upgrade_form"
                :billing_cycle="billing_cycle"
                :billing="billing" />
            </div>
            <div class="right-element">
              <AgencyPlan @choose="choosePlan" @switch="switchPlan" :show_upgrade_form="show_upgrade_form" :billing_cycle="billing_cycle" :billing="billing" />
            </div>
            <div class="content-element relative div-border-left">
              <!-- For ENTRY -->
              <iframe src="" class="full-height full-width pl-20" :class="{ hide: show_upgrade_form | (status != 'entry-active') }"></iframe>

              <!-- For GROWTH -->
              <iframe src="" class="full-height full-width pl-20" :class="{ hide: show_upgrade_form | (status != 'growth-active') }"></iframe>

              <!-- For AGENCY -->
              <iframe src="" class="full-height full-width pl-20" :class="{ hide: show_upgrade_form | (status != 'agency-active') }"></iframe>

              <Payment
                ref="payment"
                :class="{
                  hide: !show_upgrade_form | (status != 'entry-active' && status != 'growth-active' && status != 'agency-active'),
                }"
                :plan_type="plan_name + '_' + billing_cycle"
                :billing="billing"
                @cardUpdated="loadData" />
            </div>
          </div>
        </div>

        <div v-if="loading" class="absolute-inside overlay flex items-center">
          <Loading />
        </div>
      </div>
    </perfect-scrollbar>
  </div>
</template>

<script>
import "@/assets/css/embed/animate.min.css";
import request from "@/service";
import types from "@/constants/types";

import EntryPlan from "@/components/Upgrade/EntryPlan";
import GrowthPlan from "@/components/Upgrade/GrowthPlan";
import AgencyPlan from "@/components/Upgrade/AgencyPlan";
import GrowthBlackfriday from "@/components/Upgrade/GrowthBlackfriday";

import Payment from "@/components/Upgrade/Payment";
import Toggle from "@/components/Upgrade/Toggle";
import Billing from "@/components/Upgrade/Billing";
import SwitchPlan from "@/components/Upgrade/SwitchPlan";

import { is_internal_weezly } from "@/helpers/functions";
export default {
  name: "Upgrade",
  components: {
    EntryPlan,
    GrowthPlan,
    AgencyPlan,
    GrowthBlackfriday,
    Payment,
    Toggle,
    Billing,
    SwitchPlan,
  },
  props: {},
  data: function () {
    return {
      status: "",
      show_upgrade_form: false,
      loading: false,

      missing_reason: false,
      reason: "",

      billing: {
        subscription: false,
        tax: false,
        card: false,
        billing_address: false,
        invoices: [],
        packages: [],
        blackfriday: false,
      },
      plan_name: "",
      billing_cycle: "monthly",

      open_billing: false,
      open_switch: false,

      animated: false,
    };
  },
  metaInfo() {
    return {
      title: "Upgrade - Weezly",
    };
  },
  watch: {
    open_billing() {
      this.runAnimation();
    },
    open_switch() {
      this.runAnimation();
    },
  },
  async beforeMount() {
    if (!is_internal_weezly) {
      location.href = "https://app.weezly.com/upgrade";
    }
  },
  async mounted() {
    if (this.$refs.wrapper) {
      const psInstance = this.$refs.wrapper.ps;
      if (psInstance) {
        psInstance.settings.suppressScrollX = true; // Set suppressScrollX to true
      }
    }

    let canUpgrade = this.$store.getters.canUpgrade;
    if (!canUpgrade) {
      this.$root.$emit("toggle-upgrade", false);
      if (this.$route.name == "Upgrade") {
        this.$router.push("/");
      }
    }

    // Check query payment intent
    let query = this.$route.query;

    if (query.payment_intent && query.payment_intent_client_secret) {
      this.loading = true;
      const check_intent = await request("POST", "stripe/checkIntent", {
        payment_intent: query.payment_intent,
        payment_intent_client_secret: query.payment_intent_client_secret,
      });

      if (check_intent) {
        if (check_intent.data.total) {
          dataLayer.push({
            event: "purchase",
            currency: "USD",
            value: check_intent.data.total,
          });
        }

        await this.$store.dispatch({
          type: types.getMeInfo,
        });

        this.$router.push("payment-success");
      }

      this.loading = false;
    }

    this.loadData();

    // if ((this.me.plan_type == "professional_yearly") | (this.me.plan_type == "business_yearly")) {
    //   this.billing_cycle = "yearly";
    // }

    setTimeout(() => {
      if (this.$refs.wrapper) {
        const psInstance = this.$refs.wrapper.ps;
        if (psInstance) {
          psInstance.settings.suppressScrollX = true; // Set suppressScrollX to true
        }

        this.$refs.wrapper.ps.update();
      }
    }, 500);
  },
  computed: {
    me() {
      return this.$store.getters.getMe;
    },
  },
  methods: {
    runAnimation() {
      this.animated = true;
      setTimeout(() => {
        this.animated = false;
      }, 500);
    },
    async loadData() {
      this.loading = true;
      const getData = await request("GET", "stripe/getBilling");
      if (getData) {
        this.billing = getData.data;
        this.loading = false;
      } else {
        this.$root.$emit("alert", "Could not get your data. Please try again.", "error");
      }
    },
    choosePlan(name, type) {
      this.status = name + "-active";
      if (type == "detail") this.show_upgrade_form = false;
      else if (type == "upgrade") {
        this.show_upgrade_form = true;
        this.plan_name = name;
        this.$nextTick(() => {
          this.$refs.payment.focus();
          this.$refs.payment.calculate();
        });
      }
    },
    switchPlan(name) {
      this.plan_name = name;
      this.open_switch = true;
      this.$nextTick(() => {
        this.$refs.switchPlan.calculate();
      });
    },
    billingBack() {
      this.open_billing = false;
      this.$nextTick(() => {
        if (this.plan_name && this.billing_cycle) {
          if (this.$refs.payment) this.$refs.payment.calculate();
          if (this.$refs.switchPlan) this.$refs.switchPlan.calculate();
        }
      });
    },
  },
};
</script>
